import { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { useAnimationGraphActions } from './AnimationGraphContext';
import styles from './AnimStateNode.module.css';

function handleIdForTrigger(triggerId) {
  return `t:${triggerId}`;
}

function handleIdForEmotion(key) {
  return `e:${key}`;
}

function handleStyle() {
  return { right: -8, top: '50%', transform: 'translateY(-50%)' };
}

function AnimStateNode({ id, data, selected }) {
  const { triggers, attachTrigger, attachEmotion, detachTrigger, detachEmotion } = useAnimationGraphActions();
  const label = data?.label || 'Clip';
  const modelPath = data?.modelPath || '';
  const motionGroup = data?.motionGroup || '';
  const motionIndex = data?.motionIndex ?? 0;
  const triggerIds = data?.triggerIds || [];
  const emotions = data?.emotions || [];

  const triggerLabel = useCallback(
    (tid) => {
      const t = triggers.find((x) => String(x._id) === String(tid));
      return t ? `${t.name} (${t.key})` : String(tid);
    },
    [triggers]
  );

  const onDropAttach = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const tr = e.dataTransfer.getData('application/trigger');
      if (tr) {
        try {
          const o = JSON.parse(tr);
          if (o?._id) attachTrigger(id, o);
        } catch {
          /* ignore */
        }
        return;
      }
      const em = e.dataTransfer.getData('application/emotion');
      if (em) {
        try {
          const o = JSON.parse(em);
          if (o?.key) attachEmotion(id, o.key);
        } catch {
          /* ignore */
        }
      }
    },
    [id, attachTrigger, attachEmotion]
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  return (
    <div
      className={`${styles.root} ${selected ? styles.rootSelected : ''}`}
      onDrop={onDropAttach}
      onDragOver={onDragOver}
    >
      <Handle type="target" position={Position.Left} id="in" style={{ background: '#71717a' }} />

      <div className={styles.header}>
        <p className={styles.meta} title={modelPath}>
          {motionGroup} · #{motionIndex}
        </p>
        <p className={styles.title} title={label}>
          {label}
        </p>
      </div>

      <div className={styles.body}>
        {triggerIds.map((tid) => (
          <div key={tid} className={styles.row}>
            <span className={styles.rowLabel} title={triggerLabel(tid)}>
              {triggerLabel(tid)}
            </span>
            <span className={styles.arrow} aria-hidden>
              →
            </span>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => detachTrigger(id, tid)}
              title="Remove trigger"
            >
              ×
            </button>
            <Handle type="source" position={Position.Right} id={handleIdForTrigger(tid)} style={handleStyle()} />
          </div>
        ))}

        <div className={`${styles.row} ${styles.rowComplete}`}>
          <span className={styles.rowLabel}>onComplete</span>
          <span className={styles.arrow} aria-hidden>
            →
          </span>
          <Handle type="source" position={Position.Right} id="done" style={handleStyle()} />
        </div>

        {emotions.map((key) => (
          <div key={key} className={`${styles.row} ${styles.rowEmotion}`}>
            <span className={styles.rowLabel}>{key}</span>
            <span className={styles.arrow} aria-hidden>
              →
            </span>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => detachEmotion(id, key)}
              title="Remove emotion"
            >
              ×
            </button>
            <Handle type="source" position={Position.Right} id={handleIdForEmotion(key)} style={handleStyle()} />
          </div>
        ))}

        <p className={styles.hint}>Drop trigger or emotion here</p>
      </div>
    </div>
  );
}

function propsEqual(prev, next) {
  return prev.id === next.id && prev.selected === next.selected && prev.data === next.data && prev.type === next.type;
}

export default memo(AnimStateNode, propsEqual);
